/**
 * Normalizes the vehicle analysis API response so the UI works with both:
 * - Legacy shape (market_metrics, recommendation at top level or nested)
 * - New shape (only vehicle_info, market_analysis.active_listings, market_analysis.recent_sales, pricing_info, search_metadata)
 *
 * When market_analysis.market_metrics or market_analysis.recommendation are missing,
 * they are derived from active_listings and recent_sales.
 */
export function normalizeVehicleAnalysisResponse(data) {
  if (!data || typeof data !== 'object') return data;

  const out = { ...data };
  out.vehicle_info = out.vehicle_info ? { ...out.vehicle_info } : {};
  out.search_metadata = out.search_metadata ? { ...out.search_metadata } : {};
  out.market_analysis = out.market_analysis ? { ...out.market_analysis } : {};
  out.pricing_info = out.pricing_info ? { ...out.pricing_info } : {};

  const active = Array.isArray(out.market_analysis.active_listings) ? out.market_analysis.active_listings : [];
  const sold = Array.isArray(out.market_analysis.recent_sales) ? out.market_analysis.recent_sales : [];

  // Backfill vehicle_info.mileage from search_metadata for components that read vehicle_info.mileage
  if ((out.vehicle_info.mileage == null || out.vehicle_info.miles == null) && out.search_metadata.mileage != null) {
    out.vehicle_info.mileage = out.search_metadata.mileage;
    out.vehicle_info.miles = out.search_metadata.mileage;
  }

  // Derive market_metrics when missing or empty
  const existingMetrics = out.market_analysis.market_metrics;
  const hasMetrics =
    existingMetrics &&
    typeof existingMetrics === 'object' &&
    (existingMetrics.active_count != null ||
      existingMetrics.sold_count != null ||
      existingMetrics.market_avg_price != null);

  if (!hasMetrics) {
    const soldPrices = sold.map((s) => s.price).filter((p) => typeof p === 'number' && p > 0);
    const activePrices = active.map((a) => a.price).filter((p) => typeof p === 'number' && p > 0);
    const soldDom = sold.map((s) => s.dom_active).filter((d) => typeof d === 'number' && d >= 0);

    out.market_analysis.market_metrics = {
      active_count: active.length,
      sold_count: sold.length,
      market_avg_price:
        soldPrices.length > 0
          ? soldPrices.reduce((sum, p) => sum + p, 0) / soldPrices.length
          : activePrices.length > 0
            ? activePrices.reduce((sum, p) => sum + p, 0) / activePrices.length
            : null,
      avg_days_on_market:
        soldDom.length > 0 ? soldDom.reduce((sum, d) => sum + d, 0) / soldDom.length : null,
      price_range_sold:
        soldPrices.length > 0
          ? { min: Math.min(...soldPrices), max: Math.max(...soldPrices) }
          : existingMetrics?.price_range_sold ?? null,
      price_range_active:
        activePrices.length > 0
          ? { min: Math.min(...activePrices), max: Math.max(...activePrices) }
          : existingMetrics?.price_range_active ?? null,
    };
  }

  // Optional: default recommendation when missing so header can show a label
  const existingRec = out.market_analysis.recommendation;
  const hasRecommendation =
    existingRec &&
    (existingRec.recommendation != null || existingRec.action != null);
  if (!hasRecommendation) {
    out.market_analysis.recommendation = {
      recommendation: 'BUY',
      action: 'BUY',
    };
  }

  return out;
}
